import React, { useState } from "react";

const MI_TO_KM = 1.60934;

export default function TimeCalc() {
  const [distance, setDistance] = useState("");
  const [pace, setPace] = useState("");
  const [unit, setUnit] = useState("miles");
  const [time, setTime] = useState("");
  const [error, setError] = useState("");
  const [submitted, setSubmitted] = useState(false);

  const parsePaceToMinutes = (paceValue) => {
    const paceParts = paceValue.split(":");

    if (paceParts.length === 2) {
      const mins = parseInt(paceParts[0], 10);
      const secs = parseInt(paceParts[1], 10);

      if (
        Number.isNaN(mins) ||
        Number.isNaN(secs) ||
        mins < 0 ||
        secs < 0 ||
        secs >= 60
      ) {
        return { error: "Invalid pace format. Use MM:SS." };
      }

      return { value: mins + secs / 60 };
    }

    if (paceParts.length === 1) {
      const decimalMinutes = parseFloat(paceParts[0]);
      if (Number.isNaN(decimalMinutes) || decimalMinutes <= 0) {
        return { error: "Invalid pace. Use MM:SS or a decimal minute value." };
      }
      return { value: decimalMinutes };
    }

    return { error: "Invalid pace format. Use MM:SS." };
  };

  const formatMinutesToPace = (paceMinutes) => {
    let mins = Math.floor(paceMinutes);
    let secs = Math.round((paceMinutes - mins) * 60);

    if (secs >= 60) {
      mins += 1;
      secs = 0;
    }

    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const calculateTime = (distanceValue, paceValue) => {
    if (!distanceValue || !paceValue) {
      setError("Please enter both distance and pace.");
      setTime("");
      return false;
    }

    const numericDistance = parseFloat(distanceValue);
    if (Number.isNaN(numericDistance) || numericDistance <= 0) {
      setError("Distance must be a positive number.");
      setTime("");
      return false;
    }

    const parsedPace = parsePaceToMinutes(paceValue);
    if (parsedPace.error) {
      setError(parsedPace.error);
      setTime("");
      return false;
    }

    const totalTimeMinutes = numericDistance * parsedPace.value;
    let h = Math.floor(totalTimeMinutes / 60);
    let m = Math.round(totalTimeMinutes % 60);

    if (m >= 60) {
      h += 1;
      m = 0;
    }

    setError("");
    setTime(`${h}h ${m}min`);
    return true;
  };

  const handleCalculate = () => {
    setSubmitted(true);
    calculateTime(distance, pace);
  };

  const handleUnitChange = (e) => {
    const nextUnit = e.target.value;

    let convertedDistance = distance;
    const numericDistance = parseFloat(distance);
    if (!Number.isNaN(numericDistance) && numericDistance > 0) {
      const converted =
        unit === "miles" && nextUnit === "km"
          ? numericDistance * MI_TO_KM
          : unit === "km" && nextUnit === "miles"
            ? numericDistance / MI_TO_KM
            : numericDistance;

      convertedDistance = converted.toFixed(2);
      setDistance(convertedDistance);
    }

    let convertedPace = pace;
    if (pace.trim() !== "") {
      const parsedPace = parsePaceToMinutes(pace.trim());
      if (!parsedPace.error) {
        const convertedPaceMinutes =
          unit === "miles" && nextUnit === "km"
            ? parsedPace.value / MI_TO_KM
            : unit === "km" && nextUnit === "miles"
              ? parsedPace.value * MI_TO_KM
              : parsedPace.value;

        convertedPace = formatMinutesToPace(convertedPaceMinutes);
        setPace(convertedPace);
      }
    }

    setUnit(nextUnit);

    // Recalculate immediately only when a result is already being shown.
    if (submitted && !error && time) {
      calculateTime(convertedDistance, convertedPace);
    }
  };

  return (
    <article className="pw-calc-card">
      <h2 className="pw-calc-card-title">Time</h2>
      <p className="pw-calc-card-lead">
        Enter distance and target pace to estimate total time on the road.
      </p>

      <div className="pw-calc-fields">
        <div className="pw-calc-field">
          <label htmlFor="time-distance" className="pw-calc-label">
            Distance
          </label>
          <input
            id="time-distance"
            type="number"
            inputMode="decimal"
            min={0}
            step="any"
            placeholder={unit === "km" ? "e.g. 10" : "e.g. 6.2"}
            className="pw-calc-input"
            value={distance}
            onChange={(e) => setDistance(e.target.value)}
          />
        </div>
        <div className="pw-calc-field">
          <label htmlFor="time-pace" className="pw-calc-label">
            Pace (per unit)
          </label>
          <input
            id="time-pace"
            type="text"
            inputMode="text"
            placeholder="e.g. 9:15"
            className="pw-calc-input"
            value={pace}
            onChange={(e) => setPace(e.target.value)}
            autoComplete="off"
          />
        </div>
        <div className="pw-calc-field">
          <label htmlFor="time-unit" className="pw-calc-label">
            Unit
          </label>
          <select
            id="time-unit"
            className="pw-calc-select"
            value={unit}
            onChange={handleUnitChange}
          >
            <option value="miles">Miles</option>
            <option value="km">Kilometers</option>
          </select>
        </div>
      </div>

      <p
        style={{
          marginTop: "0.75rem",
          color: "var(--home-muted, #a8a8a8)",
          fontSize: "0.92rem",
          lineHeight: 1.5,
        }}
      >
        Example: You are running a half marathon (13.1 miles) at a 9:15 / mi
        pace. Enter 13.1 for distance, 9:15 for pace, select Miles, and hit
        Calculate. Your finish time would be 2h 1min.
      </p>

      <button
        type="button"
        className="pw-calc-submit"
        onClick={handleCalculate}
      >
        Calculate
      </button>

      <div className="pw-calc-results" aria-live="polite">
        {!submitted && (
          <p className="pw-calc-results-placeholder">
            Estimated time will appear here after you tap Calculate.
          </p>
        )}
        {submitted && error && (
          <p className="pw-calc-results-error">{error}</p>
        )}
        {submitted && !error && time && (
          <>
            <span className="pw-calc-results-label">Result</span>
            <p className="pw-calc-results-value">
              Estimated time: <strong>{time}</strong>
            </p>
          </>
        )}
      </div>
    </article>
  );
}
